import { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useTheme } from '../context/ThemeContext';
import { fetchWarrantyClaims, updateWarrantyClaimStatus } from '@/lib/api/forms';
import useScrollLock from '@/hooks/useScrollLock';

interface Claim {
  id: string;
  submittedAt: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  batteryType: string;
  batteryModel: string;
  serialNumber: string;
  purchaseDate: string;
  retailerName: string;
  issueType: string;
  issueDescription: string;
  vesselType: string;
  status: 'pending' | 'in_review' | 'approved' | 'rejected' | 'resolved';
}

const MOCK_CLAIMS: Claim[] = [
  {
    id: 'WC-2024-001',
    submittedAt: '2024-11-14T09:23:00Z',
    firstName: 'Captain Mike',
    lastName: 'Harrington',
    email: 'mike.harrington@example.com',
    phone: '+1 305 555 0192',
    batteryType: 'LiFePO4 Lithium',
    batteryModel: 'SR-LI-200',
    serialNumber: 'SRL200-2023-04821',
    purchaseDate: '2023-03-10',
    retailerName: 'Florida Keys Marine Supply',
    issueType: 'Premature capacity loss',
    issueDescription: 'Battery dropped to 60% capacity after only 8 months of use. Charging correctly with compatible charger. Vessel is a 35ft sportfisher used 3x per week.',
    vesselType: 'Fishing boat',
    status: 'in_review',
  },
  {
    id: 'WC-2024-002',
    submittedAt: '2024-11-10T14:05:00Z',
    firstName: 'Lars',
    lastName: 'Eriksson',
    email: 'lars.eriksson@example.se',
    phone: '+46 70 555 1234',
    batteryType: 'AGM',
    batteryModel: 'SR-AGM-100',
    serialNumber: 'SRAGM100-2022-11203',
    purchaseDate: '2022-06-22',
    retailerName: 'Nordic Marine Stockholm',
    issueType: 'Battery not holding charge',
    issueDescription: 'Battery discharges completely within 2 hours even when not under load. Purchased 2 years ago, used seasonally on a 42ft cruiser.',
    vesselType: 'Sailboat',
    status: 'approved',
  },
  {
    id: 'WC-2024-003',
    submittedAt: '2024-11-08T11:47:00Z',
    firstName: 'Sarah',
    lastName: 'Nguyen',
    email: 'sarah.nguyen@example.com.au',
    phone: '+61 2 5550 8821',
    batteryType: 'Gel',
    batteryModel: 'SR-GEL-80',
    serialNumber: 'SRGEL80-2024-00341',
    purchaseDate: '2024-01-15',
    retailerName: 'Sydney Marine Centre',
    issueType: 'Physical defect / damage',
    issueDescription: 'Noticed a small crack on the casing near the positive terminal. Battery is only 10 months old and has been stored and used correctly.',
    vesselType: 'Yacht',
    status: 'pending',
  },
  {
    id: 'WC-2024-004',
    submittedAt: '2024-11-05T08:30:00Z',
    firstName: 'Rick',
    lastName: 'Castellano',
    email: 'rick.castellano@example.com',
    phone: '+1 732 555 0044',
    batteryType: 'LiFePO4 Lithium',
    batteryModel: 'SR-LI-300',
    serialNumber: 'SRL300-2023-09912',
    purchaseDate: '2023-09-01',
    retailerName: 'Jersey Shore Marine',
    issueType: 'BMS failure',
    issueDescription: 'BMS is cutting out under moderate load. Battery shows full charge but trips the BMS protection at around 50% discharge. Charger is Stingray-compatible.',
    vesselType: 'Powerboat / Motorboat',
    status: 'resolved',
  },
  {
    id: 'WC-2024-005',
    submittedAt: '2024-11-01T16:12:00Z',
    firstName: 'Marco',
    lastName: 'Ferretti',
    email: 'marco.ferretti@example.it',
    phone: '+39 010 555 7788',
    batteryType: 'Gel',
    batteryModel: 'SR-GEL-120',
    serialNumber: 'SRGEL120-2023-05567',
    purchaseDate: '2023-05-20',
    retailerName: 'Genova Marine Electronics',
    issueType: 'Terminal / connection issue',
    issueDescription: 'Positive terminal is corroding unusually fast despite proper marine environment maintenance. Corrosion started appearing after 6 months.',
    vesselType: 'Sailboat',
    status: 'rejected',
  },
];

const STATUS_CONFIG: Record<Claim['status'], { label: string; color: string; bg: string; icon: string }> = {
  pending:   { label: 'Pending',   color: '#F59E0B', bg: '#F59E0B18', icon: 'ri-time-line' },
  in_review: { label: 'In Review', color: '#0EA5E9', bg: '#0EA5E918', icon: 'ri-search-eye-line' },
  approved:  { label: 'Approved',  color: '#10B981', bg: '#10B98118', icon: 'ri-checkbox-circle-line' },
  rejected:  { label: 'Rejected',  color: '#EF4444', bg: '#EF444418', icon: 'ri-close-circle-line' },
  resolved:  { label: 'Resolved',  color: '#8B5CF6', bg: '#8B5CF618', icon: 'ri-shield-check-line' },
};

const BATTERY_COLORS: Record<string, string> = {
  'LiFePO4 Lithium': '#10B981',
  'AGM': '#0EA5E9',
  'Gel': '#8B5CF6',
};

function buildTemplate(type: string, claim: Claim): { subject: string; body: string } {
  const name = `${claim.firstName} ${claim.lastName}`;
  const greeting = `Dear ${claim.firstName},`;
  const sig = `\n\nBest regards,\nStingray Warranty Support Team\ninfo@stingrayenergy.net\n+1 307 381 5172`;

  const templates: Record<string, { subject: string; body: string }> = {
    received: {
      subject: `Re: Warranty Claim ${claim.id} — Received`,
      body: `${greeting}\n\nThank you for submitting your warranty claim (${claim.id}) for your Stingray ${claim.batteryType} battery (Model: ${claim.batteryModel}, S/N: ${claim.serialNumber}).\n\nWe have received your claim and our warranty team is reviewing the details. You can expect a follow-up from us within 2 business days.\n\nIf you have any additional information or photos to share, please reply to this email.${sig}`,
    },
    info_request: {
      subject: `Re: Warranty Claim ${claim.id} — Additional Information Required`,
      body: `${greeting}\n\nThank you for your warranty claim (${claim.id}) regarding your Stingray ${claim.batteryType} battery (Model: ${claim.batteryModel}, S/N: ${claim.serialNumber}).\n\nTo process your claim, we need the following additional information:\n\n1. A clear photo of the battery label showing the serial number\n2. A copy of your original purchase receipt or invoice\n3. [Add specific technical question here]\n\nPlease reply to this email with the requested information at your earliest convenience.${sig}`,
    },
    approved: {
      subject: `Re: Warranty Claim ${claim.id} — Approved ✓`,
      body: `${greeting}\n\nGreat news! Your warranty claim (${claim.id}) for your Stingray ${claim.batteryType} battery (Model: ${claim.batteryModel}, S/N: ${claim.serialNumber}) has been approved.\n\nHere is what happens next:\n\n• We will ship a replacement battery to your address on file.\n• You will receive a tracking number within 1-2 business days.\n• Please retain the defective battery until we confirm the replacement has been delivered.\n\nThank you for choosing Stingray. We apologize for any inconvenience this issue may have caused.${sig}`,
    },
    rejected: {
      subject: `Re: Warranty Claim ${claim.id} — Decision`,
      body: `${greeting}\n\nThank you for submitting your warranty claim (${claim.id}) for your Stingray ${claim.batteryType} battery (Model: ${claim.batteryModel}, S/N: ${claim.serialNumber}).\n\nAfter careful review, we are unable to approve this claim under our warranty policy. The reason for this decision is:\n\n[Please specify the reason — e.g., damage caused by incompatible charger, expired warranty period, etc.]\n\nIf you believe this decision is incorrect or have additional information to provide, please reply to this email within 14 days and we will be happy to reconsider.\n\nFor out-of-warranty service options, please contact us at info@stingrayenergy.net.${sig}`,
    },
    resolved: {
      subject: `Re: Warranty Claim ${claim.id} — Resolved`,
      body: `${greeting}\n\nWe are writing to confirm that your warranty claim (${claim.id}) for your Stingray ${claim.batteryType} battery (Model: ${claim.batteryModel}, S/N: ${claim.serialNumber}) has been resolved.\n\nWe hope the resolution has been satisfactory. If you have any further questions or experience any additional issues, please do not hesitate to contact us.\n\nThank you for your patience throughout this process and for being a valued Stingray customer.${sig}`,
    },
    custom: {
      subject: `Re: Warranty Claim ${claim.id} — ${name}`,
      body: `${greeting}\n\nRegarding your warranty claim (${claim.id}) for your Stingray ${claim.batteryType} battery (Model: ${claim.batteryModel}, S/N: ${claim.serialNumber}):\n\n[Write your message here]${sig}`,
    },
  };

  return templates[type] ?? templates.custom;
}

// ── Email Compose Panel ───────────────────────────────────────────────────────
function EmailComposePanel({ claim, onClose, dark }: {
  claim: Claim;
  onClose: () => void;
  dark: boolean;
}) {
  const [templateKey, setTemplateKey] = useState('received');
  const tpl = buildTemplate(templateKey, claim);
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [copied, setCopied] = useState(false);

  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'border-white/5' : 'border-gray-100';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[var(--accent)]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[var(--accent)]';
  const fieldCls = `w-full border rounded-xl px-4 py-3 text-sm outline-none transition-colors ${inputCls}`;

  const handleTemplateChange = (key: string) => {
    setTemplateKey(key);
    const t = buildTemplate(key, claim);
    setSubject(t.subject);
    setBody(t.body);
  };

  const mailtoLink = `mailto:${claim.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(body);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const TEMPLATES = [
    { key: 'received',     label: 'Claim Received',    icon: 'ri-mail-check-line',   color: '#0EA5E9' },
    { key: 'info_request', label: 'Request Info',       icon: 'ri-question-line',     color: '#F59E0B' },
    { key: 'approved',     label: 'Approved',           icon: 'ri-checkbox-circle-line', color: '#10B981' },
    { key: 'rejected',     label: 'Rejected',           icon: 'ri-close-circle-line', color: '#EF4444' },
    { key: 'resolved',     label: 'Resolved',           icon: 'ri-shield-check-line', color: '#8B5CF6' },
    { key: 'custom',       label: 'Custom',             icon: 'ri-edit-line',         color: 'var(--accent)' },
  ];

  return (
    <div className="space-y-4">
      {/* To field */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${dark ? 'border-white/5 bg-[#0D1B2A]' : 'border-gray-100 bg-gray-50'}`}>
        <span className={`text-xs font-black uppercase tracking-widest shrink-0 ${muted}`}>To</span>
        <div className="flex items-center gap-2 flex-1">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-black shrink-0" style={{ background: 'var(--accent)' }}>
            {claim.firstName[0]}{claim.lastName[0]}
          </div>
          <span className={`text-sm font-semibold ${text}`}>{claim.firstName} {claim.lastName}</span>
          <span className={`text-xs ${muted}`}>&lt;{claim.email}&gt;</span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${dark ? 'bg-white/5 text-white/30' : 'bg-gray-200 text-gray-400'}`}>{claim.id}</span>
      </div>

      {/* Template picker */}
      <div>
        <p className={`text-xs font-black uppercase tracking-widest mb-2 ${muted}`}>Email Template</p>
        <div className="flex flex-wrap gap-2">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              onClick={() => handleTemplateChange(t.key)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap border"
              style={templateKey === t.key
                ? { background: t.color, color: 'white', borderColor: t.color }
                : { background: `${t.color}12`, color: t.color, borderColor: `${t.color}30` }
              }
            >
              <i className={`${t.icon} text-[11px]`}></i>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Subject */}
      <div>
        <p className={`text-xs font-black uppercase tracking-widest mb-1.5 ${muted}`}>Subject</p>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className={fieldCls}
        />
      </div>

      {/* Body */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className={`text-xs font-black uppercase tracking-widest ${muted}`}>Message</p>
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1.5 text-xs font-bold cursor-pointer transition-colors whitespace-nowrap ${copied ? 'text-green-400' : muted}`}
          >
            <i className={copied ? 'ri-check-line' : 'ri-clipboard-line'}></i>
            {copied ? 'Copied!' : 'Copy text'}
          </button>
        </div>
        <textarea
          rows={12}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className={`${fieldCls} resize-none font-mono text-xs leading-relaxed`}
        />
      </div>

      {/* Actions */}
      <div className={`flex items-center gap-3 pt-2 border-t ${divider}`}>
        <a
          href={mailtoLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
          style={{ background: 'var(--accent)' }}
        >
          <i className="ri-mail-send-line"></i>
          Open in Email Client
        </a>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap border transition-colors ${
            dark ? 'border-white/10 text-white/50 hover:text-white hover:border-white/20' : 'border-gray-200 text-gray-500 hover:text-gray-800'
          }`}
        >
          <i className={copied ? 'ri-check-line text-green-400' : 'ri-clipboard-line'}></i>
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button
          onClick={onClose}
          className={`px-4 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap border transition-colors ${
            dark ? 'border-white/10 text-white/50 hover:text-white hover:border-white/20' : 'border-gray-200 text-gray-500 hover:text-gray-800'
          }`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Claim Detail Modal ────────────────────────────────────────────────────────
function ClaimDetailModal({ claim, onClose, onStatusChange, dark }: {
  claim: Claim;
  onClose: () => void;
  onStatusChange: (id: string, status: Claim['status']) => void;
  dark: boolean;
}) {
  const [view, setView] = useState<'detail' | 'email'>('detail');
  useScrollLock();

  const modal = dark
    ? 'bg-[#1C2B3A] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto'
    : 'bg-white border border-gray-200 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'border-white/5' : 'border-gray-100';
  const fieldBg = dark ? 'bg-[#0D1B2A]' : 'bg-gray-50';
  const batteryColor = BATTERY_COLORS[claim.batteryType] ?? 'var(--accent)';
  const tabInactive = dark ? 'text-white/40 hover:text-white' : 'text-gray-400 hover:text-gray-700';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 pt-6 pb-4 border-b ${divider}`}>
          <div>
            <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${muted}`}>Warranty Claim</p>
            <h2 className={`text-lg font-black ${text}`}>{claim.id}</h2>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={claim.status} />
            {/* Tab switcher */}
            <div className={`flex items-center gap-1 p-1 rounded-xl ml-2 ${dark ? 'bg-white/5' : 'bg-gray-100'}`}>
              <button
                onClick={() => setView('detail')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
                  view === 'detail' ? 'text-white' : tabInactive
                }`}
                style={view === 'detail' ? { background: 'var(--accent)' } : {}}
              >
                <i className="ri-file-text-line text-[11px]"></i>
                Details
              </button>
              <button
                onClick={() => setView('email')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
                  view === 'email' ? 'text-white' : tabInactive
                }`}
                style={view === 'email' ? { background: 'var(--accent)' } : {}}
              >
                <i className="ri-mail-send-line text-[11px]"></i>
                Reply
              </button>
            </div>
            <button
              onClick={onClose}
              className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ml-1 ${dark ? 'hover:bg-white/10 text-white/40' : 'hover:bg-gray-100 text-gray-400'}`}
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        <div className="p-6">
          {view === 'detail' ? (
            <div className="space-y-5">
              {/* Claimant */}
              <div>
                <p className={`text-xs font-black uppercase tracking-widest mb-3 ${muted}`}>Claimant</p>
                <div className={`rounded-xl p-4 ${fieldBg} grid grid-cols-2 gap-3`}>
                  {[
                    { label: 'Name', value: `${claim.firstName} ${claim.lastName}` },
                    { label: 'Email', value: claim.email },
                    { label: 'Phone', value: claim.phone || '—' },
                    { label: 'Submitted', value: new Date(claim.submittedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) },
                  ].map((f) => (
                    <div key={f.label}>
                      <p className={`text-[10px] font-bold uppercase tracking-wide mb-0.5 ${muted}`}>{f.label}</p>
                      <p className={`text-sm font-medium ${text}`}>{f.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Battery */}
              <div>
                <p className={`text-xs font-black uppercase tracking-widest mb-3 ${muted}`}>Battery Information</p>
                <div className={`rounded-xl p-4 ${fieldBg} grid grid-cols-2 gap-3`}>
                  {[
                    { label: 'Battery Type', value: claim.batteryType },
                    { label: 'Model / SKU', value: claim.batteryModel },
                    { label: 'Serial Number', value: claim.serialNumber },
                    { label: 'Purchase Date', value: new Date(claim.purchaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) },
                    { label: 'Retailer', value: claim.retailerName },
                    { label: 'Vessel Type', value: claim.vesselType || '—' },
                  ].map((f) => (
                    <div key={f.label}>
                      <p className={`text-[10px] font-bold uppercase tracking-wide mb-0.5 ${muted}`}>{f.label}</p>
                      <p className={`text-sm font-medium ${text}`} style={f.label === 'Battery Type' ? { color: batteryColor } : {}}>{f.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Issue */}
              <div>
                <p className={`text-xs font-black uppercase tracking-widest mb-3 ${muted}`}>Issue Details</p>
                <div className={`rounded-xl p-4 ${fieldBg} space-y-3`}>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wide mb-0.5 ${muted}`}>Issue Type</p>
                    <p className={`text-sm font-medium ${text}`}>{claim.issueType}</p>
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold uppercase tracking-wide mb-1 ${muted}`}>Description</p>
                    <p className={`text-sm leading-relaxed ${muted}`}>{claim.issueDescription}</p>
                  </div>
                </div>
              </div>

              {/* Quick reply shortcut */}
              <div
                className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all"
                style={{ background: 'color-mix(in srgb, var(--accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}
                onClick={() => setView('email')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg text-white" style={{ background: 'var(--accent)' }}>
                    <i className="ri-mail-send-line text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: 'var(--accent)' }}>Reply to Claimant</p>
                    <p className={`text-xs ${muted}`}>{claim.email}</p>
                  </div>
                </div>
                <i className="ri-arrow-right-s-line" style={{ color: 'var(--accent)' }}></i>
              </div>

              {/* Status change */}
              <div className={`border-t ${divider} pt-5`}>
                <p className={`text-xs font-black uppercase tracking-widest mb-3 ${muted}`}>Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(STATUS_CONFIG) as Claim['status'][]).map((s) => {
                    const cfg = STATUS_CONFIG[s];
                    const active = claim.status === s;
                    return (
                      <button
                        key={s}
                        onClick={() => onStatusChange(claim.id, s)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap border"
                        style={active
                          ? { background: cfg.color, color: 'white', borderColor: cfg.color }
                          : { background: cfg.bg, color: cfg.color, borderColor: `${cfg.color}30` }
                        }
                      >
                        <i className={cfg.icon}></i>
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <EmailComposePanel
              claim={claim}
              onClose={() => setView('detail')}
              dark={dark}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: Claim['status'] }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap"
      style={{ color: cfg.color, background: cfg.bg }}
    >
      <i className={`${cfg.icon} text-[11px]`}></i>
      {cfg.label}
    </span>
  );
}

// ── Main Content ──────────────────────────────────────────────────────────────
function WarrantyClaimsContent() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [claims, setClaims] = useState<Claim[]>(MOCK_CLAIMS);
  const [filterStatus, setFilterStatus] = useState<Claim['status'] | 'all'>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Claim | null>(null);
  const [error, setError] = useState('');

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'border-white/5' : 'border-gray-100';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400';

  const filtered = claims.filter((c) => {
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchType = filterType === 'all' || c.batteryType === filterType;
    const q = search.toLowerCase();
    const matchSearch = !q || [c.id, c.firstName, c.lastName, c.email, c.batteryModel, c.serialNumber].some((v) => v.toLowerCase().includes(q));
    return matchStatus && matchType && matchSearch;
  });

  useEffect(() => {
    const load = async () => {
      try {
        const nextClaims = await fetchWarrantyClaims();
        setClaims(nextClaims);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load warranty claims.');
      }
    };

    void load();
  }, []);

  const handleStatusChange = async (id: string, status: Claim['status']) => {
    setClaims((prev) => prev.map((c) => c.id === id ? { ...c, status } : c));
    setSelected((prev) => prev?.id === id ? { ...prev, status } : prev);

    try {
      await updateWarrantyClaimStatus(id, status);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update claim status.');
    }
  };

  const counts = {
    all: claims.length,
    pending: claims.filter((c) => c.status === 'pending').length,
    in_review: claims.filter((c) => c.status === 'in_review').length,
    approved: claims.filter((c) => c.status === 'approved').length,
    rejected: claims.filter((c) => c.status === 'rejected').length,
    resolved: claims.filter((c) => c.status === 'resolved').length,
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {([
          { key: 'all',       label: 'Total',     icon: 'ri-file-list-3-line',    color: 'var(--accent)' },
          { key: 'pending',   label: 'Pending',   icon: 'ri-time-line',           color: '#F59E0B' },
          { key: 'in_review', label: 'In Review', icon: 'ri-search-eye-line',     color: '#0EA5E9' },
          { key: 'approved',  label: 'Approved',  icon: 'ri-checkbox-circle-line',color: '#10B981' },
          { key: 'rejected',  label: 'Rejected',  icon: 'ri-close-circle-line',   color: '#EF4444' },
          { key: 'resolved',  label: 'Resolved',  icon: 'ri-shield-check-line',   color: '#8B5CF6' },
        ] as { key: 'all' | Claim['status']; label: string; icon: string; color: string }[]).map((s) => (
          <button
            key={s.key}
            onClick={() => setFilterStatus(s.key)}
            className={`${card} rounded-xl p-4 text-left cursor-pointer transition-all ${filterStatus === s.key ? 'outline outline-2' : ''}`}
            style={filterStatus === s.key ? { outlineColor: s.color } : {}}
          >
            <div className="flex items-center justify-between mb-2">
              <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
              {filterStatus === s.key && <div className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }}></div>}
            </div>
            <p className="text-xl font-black" style={{ color: s.color }}>{counts[s.key]}</p>
            <p className={`text-xs ${muted}`}>{s.label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className={`${card} rounded-xl p-4 flex flex-wrap gap-3 items-center`}>
        <div className="flex-1 min-w-48">
          <div className="relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${muted}`}></i>
            <input
              type="text"
              placeholder="Search by name, email, serial, model..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full border rounded-lg pl-9 pr-4 py-2.5 text-sm outline-none ${inputCls}`}
            />
          </div>
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className={`border rounded-lg px-3 py-2.5 text-sm outline-none cursor-pointer ${inputCls}`}
        >
          <option value="all">All Battery Types</option>
          <option value="LiFePO4 Lithium">LiFePO4 Lithium</option>
          <option value="AGM">AGM</option>
          <option value="Gel">Gel</option>
        </select>
        <span className={`text-xs ${muted} whitespace-nowrap`}>{filtered.length} claim{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Claims table */}
      <div className={`${card} rounded-xl overflow-hidden`}>
        <div className={`grid grid-cols-[1fr_1.2fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 border-b ${divider} text-[10px] font-black uppercase tracking-widest ${muted}`}>
          <span>Claim ID</span>
          <span>Claimant</span>
          <span>Battery</span>
          <span>Issue</span>
          <span>Status</span>
          <span></span>
        </div>

        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <i className={`ri-shield-check-line text-3xl mb-3 block ${muted}`}></i>
            <p className={`text-sm ${muted}`}>No claims match your filters.</p>
          </div>
        ) : (
          filtered.map((claim) => {
            const batteryColor = BATTERY_COLORS[claim.batteryType] ?? 'var(--accent)';
            return (
              <div
                key={claim.id}
                className={`grid grid-cols-[1fr_1.2fr_1fr_1fr_1fr_auto] gap-4 px-5 py-4 border-b ${divider} items-center transition-colors`}
              >
                <div>
                  <p className={`text-xs font-black ${text}`}>{claim.id}</p>
                  <p className={`text-[10px] ${muted}`}>{new Date(claim.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <div>
                  <p className={`text-sm font-semibold ${text}`}>{claim.firstName} {claim.lastName}</p>
                  <p className={`text-xs ${muted} truncate`}>{claim.email}</p>
                </div>
                <div>
                  <p className="text-xs font-bold" style={{ color: batteryColor }}>{claim.batteryType}</p>
                  <p className={`text-[10px] ${muted}`}>{claim.batteryModel}</p>
                </div>
                <div>
                  <p className={`text-xs ${text} truncate`}>{claim.issueType}</p>
                  <p className={`text-[10px] ${muted}`}>{claim.vesselType || '—'}</p>
                </div>
                <div>
                  <StatusBadge status={claim.status} />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelected(claim)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-colors"
                    style={{ background: 'color-mix(in srgb, var(--accent) 12%, transparent)', color: 'var(--accent)' }}
                  >
                    <i className="ri-eye-line text-[11px]"></i>
                    View
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setSelected(null)} />
          <ClaimDetailModal
            claim={selected}
            onClose={() => setSelected(null)}
            onStatusChange={handleStatusChange}
            dark={dark}
          />
        </>
      )}
    </div>
  );
}

export default function WarrantyClaimsPage() {
  return (
    <AdminLayout title="Warranty Claims">
      <WarrantyClaimsContent />
    </AdminLayout>
  );
}
